import axios from 'axios';

const CONSENT_VERSION = '2026-04-gdpr-v1';

function defaultConsentState() {
    return {
        consentVersion: CONSENT_VERSION,
        consentGiven: false,
        consentGivenAt: null,
        consents: {
            essential: true,
            youtube: false,
        },
        preferencesOpen: false,
        isHydrated: false,
    };
}

function normalizeBoolean(value, fallback = false) {
    if (value === undefined || value === null) {
        return fallback;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true' || normalized === '1') {
            return true;
        }
        if (normalized === 'false' || normalized === '0' || normalized === '') {
            return false;
        }
    }

    return Boolean(value);
}

function buildConsentStateFromUser(user) {
    const baseState = defaultConsentState();
    const consentGiven = Boolean(user?.cookie_consented_at);

    return {
        ...baseState,
        consentVersion: String(user?.cookie_consent_version || CONSENT_VERSION),
        consentGiven,
        consentGivenAt: typeof user?.cookie_consented_at === 'string' ? user.cookie_consented_at : null,
        consents: {
            essential: true,
            youtube: consentGiven ? normalizeBoolean(user?.youtube_cookies_enabled, false) : false,
        },
        preferencesOpen: false,
        isHydrated: true,
    };
}

export default {
    namespaced: true,
    state: () => defaultConsentState(),
    mutations: {
        syncFromUser(state, user) {
            Object.assign(state, buildConsentStateFromUser(user));
        },
        resetState(state) {
            Object.assign(state, {
                ...defaultConsentState(),
                isHydrated: true,
            });
        },
        setPreferencesOpen(state, value) {
            state.preferencesOpen = normalizeBoolean(value, false);
        },
    },
    actions: {
        hydrateConsent({ commit, rootState }) {
            const isAuthenticated = normalizeBoolean(rootState.user?.isAuthenticated, false);
            const user = rootState.user?.user;

            if (!isAuthenticated || !user) {
                commit('resetState');
                return;
            }

            commit('syncFromUser', user);
        },
        syncFromUser({ commit }, user) {
            if (!user) {
                commit('resetState');
                return;
            }

            commit('syncFromUser', user);
        },
        resetConsentState({ commit }) {
            commit('resetState');
        },
        async persistConsent({ dispatch, rootState }, preferences) {
            if (!normalizeBoolean(rootState.user?.isAuthenticated, false)) {
                return null;
            }

            const response = await axios.patch('/me/cookie-consent', {
                consent_version: CONSENT_VERSION,
                youtube_cookies_enabled: normalizeBoolean(preferences?.youtube, false),
            });

            if (response?.data?.user) {
                await dispatch('user/setUser', response.data.user, { root: true });
                return response.data.user;
            }

            return null;
        },
        acceptAll({ dispatch }) {
            return dispatch('persistConsent', { youtube: true });
        },
        rejectOptional({ dispatch }) {
            return dispatch('persistConsent', { youtube: false });
        },
        savePreferences({ dispatch }, preferences) {
            return dispatch('persistConsent', {
                youtube: preferences?.youtube,
            });
        },
        openPreferences({ commit, rootState }) {
            if (!normalizeBoolean(rootState.user?.isAuthenticated, false)) {
                return;
            }

            commit('setPreferencesOpen', true);
        },
        closePreferences({ commit }) {
            commit('setPreferencesOpen', false);
        },
    },
    getters: {
        shouldShowBanner: (state, getters, rootState) => {
            const isAuthenticated = normalizeBoolean(rootState.user?.isAuthenticated, false);
            if (!isAuthenticated || !state.isHydrated) {
                return false;
            }

            return !state.consentGiven || state.preferencesOpen;
        },
        isYoutubeEnabled: (state, getters, rootState) => {
            const isAuthenticated = normalizeBoolean(rootState.user?.isAuthenticated, false);
            if (!isAuthenticated || !state.isHydrated) {
                return false;
            }

            if (!state.consentGiven) {
                return false;
            }

            return normalizeBoolean(state.consents?.youtube, false);
        },
        currentPreferences: state => ({
            essential: true,
            youtube: normalizeBoolean(state.consents?.youtube, false),
        }),
        consentVersion: state => state.consentVersion,
    },
};